import { EventrDate, setUserOrgPref, spLog } from '@eventr/common'
import {
    ColoursObj,
    DateTimePicker,
    Gutters,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack, Switch } from '@mui/material'
import { FC, useMemo } from 'react'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { getAllDayOfDate } from '../../../utils'
import { logEvent } from '../../firebase/firebase'
import { NavigationView } from '../../layout/NavigationView'
import { useGuestStore } from '../guest.store'
import {
    useDuplicateContext,
    useDuplicateStore,
} from '../state/duplicate.context'

import { useShallow } from 'zustand/react/shallow'
import { Paths } from '../../../routes'

export const SelectDateTimeRangeImportPage: FC = () => {
    const duplicateContext = useDuplicateStore()

    const { range, selectedOrg } = useDuplicateContext(
        useShallow((s) => ({
            range: s.range,
            selectedOrg: s.selectedOrg,
            selectedItin: s.selectedItin,
        }))
    )
    const guestData = useGuestStore((s) => s.guestData)
    const { getLanguageISO639Code } = useLanguageContext()
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const footer = (
        <Stack padding="16px">
            <MainButton
                id="duplicate-itinerary-date-range-duplicate-button"
                style={{ width: '100%' }}
                onClick={async () => {
                    replaceDialog({
                        node: (
                            <MobileConfirmDialog
                                text={i18n.t('guest.duplicateItineraryConfirm')}
                                confirmText={i18n.t('guest.duplicate')}
                                confirmBtnTextColour={ColoursObj.core.secondary}
                                cancelBtnTextColour={ColoursObj.states.error}
                                onCancel={() => {
                                    logEvent('guest_dup_cancel', {
                                        code: useGuestStore.getState()
                                            .guestCode,
                                    })
                                    clearDialog()
                                }}
                                onConfirm={async () => {
                                    spLog('duplicate starting')
                                    try {
                                        loadingSignal.value = true
                                        logEvent('guest_dup_confirm', {
                                            code: useGuestStore.getState()
                                                .guestCode,
                                        })
                                        const resp = await duplicateContext
                                            .getState()
                                            .duplicateItinerary()

                                        /// Check we got desired responce.
                                        if (resp.status === 'error') {
                                            clearDialog()
                                            defaultAddSnack(
                                                'Network error!',
                                                'error'
                                            )
                                            return
                                        }

                                        useGuestStore.setState(
                                            useGuestStore.getInitialState()
                                        )
                                        await bs().setSelectedOrg(selectedOrg)

                                        await bs().setCurrentItin(resp.data)
                                        setUserOrgPref(
                                            'itinerary',
                                            'defaultItinerary',
                                            resp.data.toString(),
                                            selectedOrg.name
                                        )
                                        clearDialog()
                                        window.pushPath(Paths.home)
                                    } catch (e) {
                                        console.error(e)
                                    } finally {
                                        loadingSignal.value = false
                                    }
                                }}
                            />
                        ),
                        backdropClosable: false,
                    })
                }}
                text={i18n.t('guest.duplicate')}
                disabled={
                    range.all ? false : !range.endDate || !range.startDate
                }
            />
        </Stack>
    )

    const selectedActivities = useMemo(() => {
        if (range.all) {
            return []
        }

        try {
            const tmpActivities = structuredClone(
                guestData.itinerary.v2.find((v) => v.sectionID === 8).data
                    .activities
            )

            if ((tmpActivities?.length ?? 0) < 1) {
                return []
            }

            let actList = []
            for (const activity of tmpActivities) {
                /// these will use selected itin tz if set  to N... will need to be fixed
                const startTime = new EventrDate(activity.start)

                if (
                    !activity.start ||
                    !startTime.isValid ||
                    startTime.getUnixMs <
                        new EventrDate(range.startDate).getUnixMs ||
                    startTime.getUnixMs >
                        new EventrDate(range.endDate).getUnixMs
                ) {
                    /// is before desired time or after...
                    continue
                }

                /// else is good to add to list
                actList.push(activity)
            }

            return actList
        } catch (error) {
            console.error(error)
            return []
        }
    }, [range])

    const activityListLength = useMemo(() => {
        try {
            return (
                guestData?.itinerary?.v2.find(
                    (section) => section.sectionID === 8
                ).data.activities ?? []
            ).length.toString()
        } catch {
            return '0'
        }
    }, [guestData])

    return (
        <NavigationView
            navigationBarTitle={i18n.t('guest.duplicateItinerary')}
            navigationBarSubtitle={i18n.t('guest.selectDateRangeSubTitle')}
            backButtonAction={() => {
                window.goBack()
            }}
            usePageTransition
            footer={footer}
        >
            <Gutters>
                <Box display={'grid'} gap={'8px'}>
                    <Typography variant="bodyBold">
                        {i18n.t('guest.selectDateRange')}
                    </Typography>

                    <Box display={'grid'}>
                        <DateTimePicker
                            fieldLabel={i18n.t('guest.startDate')}
                            eventrDateString={range.startDate}
                            onChange={(dateTimeString) => {
                                duplicateContext.getState().setRange({
                                    ...range,
                                    startDate: dateTimeString,
                                })
                            }}
                            disabled={range.all}
                            locale={getLanguageISO639Code()}
                            saveText={i18n.t('general.done')}
                            clearText={i18n.t('general.clear')}
                            minuteText={i18n.t('general.minute')}
                            hourText={i18n.t('general.hour')}
                            selectDateText={i18n.t(
                                'edit-itinerary.fields.select-date'
                            )}
                            selectTimeText={i18n.t(
                                'edit-itinerary.fields.select-time'
                            )}
                        />
                        <DateTimePicker
                            fieldLabel={i18n.t('guest.endDate')}
                            eventrDateString={range.endDate}
                            onChange={(dateTimeString) => {
                                duplicateContext.getState().setRange({
                                    ...range,
                                    endDate: dateTimeString,
                                })
                            }}
                            disabled={range.all}
                            locale={getLanguageISO639Code()}
                            saveText={i18n.t('general.done')}
                            clearText={i18n.t('general.clear')}
                            minuteText={i18n.t('general.minute')}
                            hourText={i18n.t('general.hour')}
                            selectDateText={i18n.t(
                                'edit-itinerary.fields.select-date'
                            )}
                            selectTimeText={i18n.t(
                                'edit-itinerary.fields.select-time'
                            )}
                        />
                    </Box>

                    <Box display={'grid'} padding={'16px 0 16px 0'}>
                        <Typography variant="bodyBold">
                            {i18n.t('general.or')}
                        </Typography>
                    </Box>

                    <Box display={'grid'} gridTemplateColumns={'1fr auto'}>
                        <Typography>
                            {i18n.t('guest.allActivities')}
                            {' ('}
                            {activityListLength}
                            {')'}
                        </Typography>
                        <Switch
                            checked={range.all}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    duplicateContext.getState().setRange({
                                        all: true,
                                        startDate: getAllDayOfDate(
                                            guestData?.itinerary?.v2[0].data
                                                .start_date,
                                            'start'
                                        ),
                                        endDate: getAllDayOfDate(
                                            guestData?.itinerary?.v2[0].data
                                                .end_date,
                                            'end'
                                        ),
                                    })
                                } else {
                                    duplicateContext.getState().setRange({
                                        all: false,
                                        startDate: getAllDayOfDate(
                                            guestData?.itinerary?.v2[0].data
                                                .start_date,
                                            'start'
                                        ),
                                        endDate: getAllDayOfDate(
                                            guestData?.itinerary?.v2[0].data
                                                .end_date,
                                            'end'
                                        ),
                                    })
                                }
                            }}
                        />
                    </Box>

                    <Typography variant="bodyBold">
                        {i18n.t('guest.activitiesTBeCopied', {
                            activityCount: range.all
                                ? activityListLength
                                : selectedActivities.length,
                        })}
                    </Typography>
                </Box>
            </Gutters>
        </NavigationView>
    )
}
